import classNames from "classnames";
import { Field, Form, Formik, getIn } from "formik";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { object, string } from "yup";

import { PasswordChange, User } from "pwapi";

import {
  saveProfile,
  userMe,
  resetSaveProfileErrors,
  userUnsetMFA,
} from "shared/actions/authActions";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import PhoneInput from "components/customInputs/PhoneInput";
import Spinner from "components/Spinner";
import ApplicationState from "store/ApplicationState";
import { AppDispatch } from "store/store";

import TextInput from "../customInputs/TextInput";
import Layout from "../Layout";
import MfaMethodSelectionForm from "../MfaMethodSelectionForm";
import PasswordChangeFormFields, {
  passwordMatchSchema,
  PasswordRequirements,
} from "../PasswordChangeFormFields";

import "./SettingsPage.css";

type SettingsPageProps = {
  setting?: "profile" | "security";
};

const SettingsPage = ({ setting = "profile" }: SettingsPageProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user = undefined } = useSelector(
    ({ auth }: ApplicationState) => auth
  );
  const features = useSelector(({ sys }: ApplicationState) => sys.features);
  const loadingUser = useSelector(
    ({ requestStatuses }: ApplicationState) =>
      requestStatuses.loading[userMe.getRequestKey()]
  );
  const updateErrors = useSelector(
    ({ requestStatuses }: ApplicationState) =>
      requestStatuses.errors["user/me/update"]
  );
  const mfaEnabled = !!user?.userprofile?.mfa_method;

  // Extends User to have new_password1 and new_password2 fields
  type ProfileFormData = User & PasswordChange;

  const schema = object().shape({
    email: string().required(),
    first_name: string().required(),
    last_name: string().required(),
    ...passwordMatchSchema(false),
  });

  const handleSave = (data: ProfileFormData) => {
    const { new_password1, new_password2, ...newUserValues } = data;
    dispatch(saveProfile(newUserValues, { new_password1, new_password2 }));
  };

  // TODO loading state
  if (!user || loadingUser) return <Spinner />;

  return (
    <Layout
      requisitionsNav={
        user?.has_access_to_report_list && features?.clinicRequisitionsList
      }
      reportsNav={
        user?.has_access_to_report_list && features?.clinicReportsList
      }
    >
      <Helmet>
        <title>Profile - Pathology Watch</title>
      </Helmet>
      <div className="SettingsPage__layout">
        <nav className="SettingsPage__tabs">
          <NavLink
            to="/settings/profile"
            className={() =>
              classNames("SettingsPage__tab", {
                "SettingsPage__tab--active": setting === "profile",
              })
            }
            end
          >
            Profile
          </NavLink>
          <NavLink
            to="/settings/security"
            className={({ isActive }) =>
              classNames("SettingsPage__tab", {
                "SettingsPage__tab--active": setting === "security" || isActive,
              })
            }
          >
            Security
          </NavLink>
        </nav>
        {setting === "profile" && (
          <div className="SettingsPage__details">
            <h1 className="SettingsPage__header">Profile</h1>
            <hr />
            <Formik
              initialValues={{
                ...user,
                new_password1: "",
                new_password2: "",
              }}
              validationSchema={schema}
              onSubmit={handleSave}
            >
              {({ handleChange, errors }) => {
                const onChange = (e: any) => {
                  if (getIn(updateErrors, e.target.name || e.target.id)) {
                    dispatch(
                      resetSaveProfileErrors(e.target.name || e.target.id)
                    );
                  }
                  handleChange(e);
                };
                return (
                  <>
                    <Form className="SettingsPage__form">
                      <div className="SettingsPage__profile">
                        {/*
                      Cannot allow edits to username for now; if a new username
                      arrives in the request body of the PATCH, the back end
                      applies the change so swiftly that the PATCH response is
                      401 Unauthorized since you're now logged in with a stale username.
                      You would think this wouldn't matter until the next login
                      but it shuts down all communication with the server immediately.
                      */}
                        <div className="CustomInput CustomInput__container CustomInput--readonly">
                          <div className="inputLabel__container">
                            <label>Username</label>
                          </div>
                          <strong>{user.username}</strong>
                        </div>
                        <Field name="userprofile.title">
                          {({ field }: any) => (
                            <TextInput
                              label="Title"
                              id="title"
                              {...field}
                              onChange={onChange}
                            />
                          )}
                        </Field>
                        <Field name="first_name">
                          {({ field }: any) => (
                            <TextInput
                              required
                              label="First Name"
                              id="first_name"
                              {...field}
                              onChange={onChange}
                            />
                          )}
                        </Field>
                        <Field name="last_name">
                          {({ field }: any) => (
                            <TextInput
                              required
                              label="Last Name"
                              id="last_name"
                              {...field}
                              onChange={onChange}
                            />
                          )}
                        </Field>
                        <Field name="email">
                          {({ field }: any) => (
                            <TextInput
                              required
                              label={`Email${
                                user.userprofile?.mfa_method === "email"
                                  ? "*"
                                  : ""
                              }`}
                              id="email"
                              type="email"
                              {...field}
                              onChange={onChange}
                              error={
                                updateErrors?.email || getIn(errors, "email")
                              }
                              disabled={
                                user.userprofile?.mfa_method === "email"
                              }
                            />
                          )}
                        </Field>
                        <Field name="userprofile.phone">
                          {({ field }: any) => (
                            <PhoneInput
                              required
                              label={`Phone${
                                user.userprofile?.mfa_method === "sms"
                                  ? "*"
                                  : ""
                              }`}
                              id="phone"
                              {...field}
                              onChange={onChange}
                              error={
                                updateErrors?.userprofile?.phone ||
                                getIn(errors, "userprofile.phone")
                              }
                              disabled={user.userprofile?.mfa_method === "sms"}
                            />
                          )}
                        </Field>
                      </div>
                      {user.userprofile?.mfa_method &&
                        ["email", "sms"].includes(
                          user.userprofile?.mfa_method
                        ) && (
                          <div className="mt-2">
                            <em>
                              <small>
                                *Cannot change
                                {user.userprofile.mfa_method === "sms"
                                  ? " phone while SMS "
                                  : " email while it "}
                                is selected as authentication method
                              </small>
                            </em>
                          </div>
                        )}
                      <footer>
                        <PathologyWatchButton
                          type="submit"
                          title="Save"
                          color="blue"
                        >
                          Save
                        </PathologyWatchButton>
                      </footer>
                    </Form>
                  </>
                );
              }}
            </Formik>
          </div>
        )}
        {setting === "security" && (
          <div className="SettingsPage__details">
            <h1 className="SettingsPage__header">Security</h1>
            <hr />
            <Formik
              initialValues={{
                ...user,
                new_password1: "",
                new_password2: "",
              }}
              validationSchema={schema}
              onSubmit={handleSave}
            >
              {({ handleChange, errors, touched }) => {
                const onChange = (e: any) => {
                  if (getIn(updateErrors, e.target.name || e.target.id)) {
                    dispatch(
                      resetSaveProfileErrors(e.target.name || e.target.id)
                    );
                  }
                  handleChange(e);
                };
                return (
                  <>
                    <Form
                      id="settingsSecurityForm"
                      className="SettingsPage__form"
                    >
                      <h2 className="SettingsPage__subheader">
                        Change Password
                      </h2>
                      <div className="SettingsPage__profile">
                        <PasswordChangeFormFields
                          labels
                          handleChange={onChange}
                          errors={errors}
                          touched={touched}
                        />
                      </div>
                      <div className="SettingsPage__requirements">
                        <PasswordRequirements />
                      </div>
                      <footer>
                        <PathologyWatchButton
                          type="submit"
                          form="settingsSecurityForm"
                          title="Save"
                          color="blue"
                        >
                          Save
                        </PathologyWatchButton>
                      </footer>
                      <hr />
                    </Form>
                    <div className="SettingsPage__form">
                      <h2 className="SettingsPage__subheader">
                        Authentication Method
                      </h2>
                      <div className="my-3">
                        Current:{" "}
                        <strong>
                          {user?.userprofile?.mfa_method
                            ? {
                                email: "Email",
                                sms: "SMS",
                                app: "Authenticator App",
                              }[user.userprofile.mfa_method]
                            : "None"}
                        </strong>
                      </div>
                      <label>Change Authentication Method</label>
                      <MfaMethodSelectionForm canCancel={true} />
                      {mfaEnabled && (
                        <PathologyWatchButton
                          onClick={() =>
                            user.id && dispatch(userUnsetMFA(user.id))
                          }
                          color="red"
                        >
                          Remove Authentication Method
                        </PathologyWatchButton>
                      )}
                    </div>
                  </>
                );
              }}
            </Formik>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SettingsPage;
